import { useState, useRef, useEffect } from 'react';
import { saveLesson } from '../utils/lessonsService';
import { upsertLibraryLesson } from '../assets/data';

// Extracted from LessonWorkspace.js (Phase 3 of the platform-health plan:
// getting large files under a saner line count needs pulling logic into
// hooks, not just JSX into components). This is the single riskiest
// extraction of the batch — the lesson save function plus the entire
// unsaved-changes guard system built around it (dirty tracking, Ctrl+S,
// 30s autosave, beforeunload warning, browser-back interception, and the
// global window.__novasUnsavedChangesGuard other UI calls before
// navigating) — moved byte-for-byte identical, including the internal
// refs' exact effect-dependency arrays, since their ordering/timing is
// load-bearing (e.g. handleSaveRef.current = handleSave must be
// reassigned on every render, not inside an effect, so Ctrl+S/autosave
// always call the latest closure).
export function useLessonSaveGuard({
  isStudent,
  lessonTitle,
  studentId,
  lessonId,
  mainEditor,
  editorContents,
  whiteboardContents,
  containers,
  docContainers,
  homeworkContainers,
  homeworkDataCache,
  setHomeworkDataCache,
  whiteboards,
  containerComments,
  insertedRoundVideos,
  insertedRoundAudios,
  insertedRoundTexts,
  bannerImage,
  docBanners,
  homeworkBanners,
  navigate,
  showLessonSaveToast,
  setSaveStatus,
  isLoading,
  setShowSaveChangesModal,
  pendingNavigation,
  setPendingNavigation,
  searchParams,
  homeworkId,
}) {
  const [addSnapshotToLibrary, setAddSnapshotToLibrary] = useState(false);

  const handleSaveRef = useRef(null);
  // Guard against concurrent saves: when a new lesson is being created,
  // the first save's INSERT must complete and the URL must flip to
  // /lessons/{uuid} before a second save runs — otherwise the second
  // call would also see lessonId === 'new' and create a second row.
  const savingRef = useRef(false);

  const handleSave = async ({ silent = false, skipNavigate = false } = {}) => {
    // saveLesson() is teacher-only (lessonsService.js enforces this
    // server-side too) — a student session hitting this always throws
    // "Only teachers can save lessons". The 30s autosave interval and
    // Ctrl+S below have no isStudent guard of their own, so a student
    // idly leaving a lesson/homework tab open was retrying and failing
    // this every 30 seconds indefinitely. Homework has its own save path
    // (submitHomework); students never need this one.
    if (isStudent) {
      if (!silent) console.log('handleSave: skipping — students save via submitHomework, not saveLesson');
      return;
    }
    if (savingRef.current) {
      if (!silent) console.log('handleSave: skipping — another save is in flight');
      return;
    }
    if (!lessonTitle.trim()) {
      if (!silent) showLessonSaveToast('error', 'Please enter a lesson title before saving.');
      return;
    }

    if (!studentId) {
      if (!silent) showLessonSaveToast('error', 'Select a student before saving.');
      return;
    }

    savingRef.current = true;
    setSaveStatus('saving');

    // Собираем содержимое из всех редакторов при сохранении
    const editorContentsArray = [];

    // Собираем содержимое из основного редактора
    if (mainEditor) {
      const editorContent = mainEditor.getHTML();
      editorContentsArray.push({
        id: 'main-editor',
        type: 'editor',
        content: editorContent
      });
    }

    // Собираем содержимое из всех контейнеров (используем сохраненное состояние)
    Object.entries(editorContents).forEach(([editorId, content]) => {
      if (editorId !== 'main-editor') { // Основной редактор уже добавлен выше
        editorContentsArray.push({
          id: editorId,
          type: 'editor',
          content: content
        });
      }
    });

    // Собираем содержимое досок (используем сохраненное состояние)
    const whiteboardContentsArray = [];
    Object.entries(whiteboardContents).forEach(([whiteboardId, content]) => {
      whiteboardContentsArray.push({
        id: whiteboardId,
        type: 'whiteboard',
        content: content
      });
    });

    // Собираем данные урока
    const lessonData = {
      id: lessonId !== 'new' ? lessonId : undefined, // Сохраняем ID если редактируем существующий урок
      studentId: studentId,
      title: lessonTitle,
      bannerImage: bannerImage && bannerImage.startsWith('data:image/') ? bannerImage : null, // Сохраняем только base64 баннеры
      docBanners: Object.fromEntries(
        Object.entries(docBanners).filter(([_, banner]) => banner && banner.startsWith('data:image/'))
      ), // Сохраняем только base64 doc баннеры
      homeworkBanners: Object.fromEntries(
        Object.entries(homeworkBanners).filter(([_, banner]) => banner && banner.startsWith('data:image/'))
      ), // Сохраняем только base64 homework баннеры
      containers: containers,
      docContainers: docContainers, // Добавляем контейнеры doc табов
      homeworkContainers: homeworkContainers, // Добавляем контейнеры домашних заданий
      // Persist title / description / dueDate / bannerImage that the
      // teacher typed into homework tabs even before clicking Send.
      // Without this, switching away and coming back to the lesson
      // dropped the metadata on the floor for any homework that
      // didn't yet have a Supabase row.
      homeworkDataCache: homeworkDataCache,
      whiteboards: whiteboards,
      editorContents: editorContentsArray, // Добавляем содержимое редакторов
      whiteboardContents: whiteboardContentsArray, // Добавляем содержимое досок
      containerComments: containerComments,
      insertedRoundVideos: insertedRoundVideos.map(({ id, containerKey, videoUrl, position }) => ({ id, containerKey, videoUrl, position })),
      insertedRoundAudios: insertedRoundAudios.map(({ id, containerKey, audioUrl, position }) => ({ id, containerKey, audioUrl, position })),
      insertedRoundTexts: insertedRoundTexts.map(({ id, containerKey, body, position }) => ({ id, containerKey, body, position })),
      // Не добавляем date и status - уроки из LessonWorkspace не должны попадать в расписание
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };



    // Проверяем размер баннера
    if (bannerImage && bannerImage.length > 5 * 1024 * 1024) { // 5MB
      console.warn('Banner image is too large (>5MB), this might cause issues');
    }

    // Сохраняем урок
    try {
      const savedLesson = await saveLesson(lessonData);

      // Always upsert/update a per-student representation in the library (kept in sync)
      try {
        const currentTeacherId = localStorage.getItem('novas-current-teacher') || 'teacher-1';
        const instTemplate = {
          id: `inst-${savedLesson.id}`,
          title: lessonData.title,
          description: '',
          cover: lessonData.bannerImage || '',
          topics: [],
          cefrLevels: ['B1'],
          medium: 'text',
          price: { type: 'free' },
          visibility: 'private',
          isTemplate: true,
          authorId: String(currentTeacherId),
          originalId: savedLesson.id,
          studentId: String(savedLesson.studentId || studentId || ''),
          purchasedBy: [],
        };
        upsertLibraryLesson(instTemplate);
      } catch (_) {}

      // If requested, add a clean snapshot (not linked to student) to Lesson Library once
      if (addSnapshotToLibrary) {
        try {
          const currentTeacherId = localStorage.getItem('novas-current-teacher') || 'teacher-1';
          const snapshot = {
            id: `snap-${savedLesson.id}-${Date.now()}`,
            title: lessonData.title,
            description: '',
            cover: lessonData.bannerImage || '',
            topics: [],
            cefrLevels: ['B1'],
            medium: 'text',
            price: { type: 'free' },
            visibility: 'private',
            isTemplate: true,
            authorId: String(currentTeacherId),
            originalId: savedLesson.id,
            purchasedBy: [],
          };
          upsertLibraryLesson(snapshot);
        } catch (_) {}
        setAddSnapshotToLibrary(false);
      }
      setSaveStatus('');
      lessonDirtyRef.current = false; // saved — no longer dirty

      // If this was a new lesson, replace the /lessons/new URL with the real UUID so
      // browser Back doesn't land on a blank /new page and lose all content.
      if (lessonId === 'new' && savedLesson.id) {
        const studentQs = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';
        navigate(`/lessons/${savedLesson.id}${studentQs}`, { replace: true });
      }

      window.dispatchEvent(new CustomEvent('lessonDataUpdated', {
        detail: { lessonId: savedLesson.id }
      }));

      if (!silent) {
        if (studentId && !isStudent && !skipNavigate) {
          showLessonSaveToast('success', 'Lesson saved successfully!', 1400);
          setTimeout(() => navigate(`/students/${studentId}`), 1400);
        } else {
          showLessonSaveToast('success', 'Lesson saved successfully!', 3500);
        }
      }
    } catch (error) {
      console.error('Error saving lesson:', error);
      setSaveStatus('');
      if (!silent) showLessonSaveToast('error', error?.message || 'Could not save lesson. Try again.');
    } finally {
      savingRef.current = false;
    }
  };

  // Keep the ref current so event-listener closures always call the latest version
  handleSaveRef.current = handleSave;

  // Слушаем события от бокового меню для показа модального окна сохранения
  useEffect(() => {
    const handleCheckUnsavedChanges = (e) => {
      const { navigate: navigateFn } = e.detail || {};
      if (!navigateFn || typeof navigateFn !== 'function') return;
      // Always claim the event so the dispatcher knows we handled it
      e.preventDefault();
      if (!lessonDirtyRef.current) {
        // No edits — let navigation proceed immediately
        navigateFn();
        return;
      }
      setShowSaveChangesModal(true);
      setPendingNavigation(() => navigateFn);
    };

    window.addEventListener('checkUnsavedChanges', handleCheckUnsavedChanges);
    return () => {
      window.removeEventListener('checkUnsavedChanges', handleCheckUnsavedChanges);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Unsaved changes guard ---
  // Tracks whether the lesson has edits since the last save / load. Used to
  // prompt the user before they navigate away (back gesture, tab close, etc).
  const lessonDirtyRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // After the initial load completes, any subsequent state change marks dirty.
  useEffect(() => {
    if (isLoading) return;
    // First time we see isLoading=false, treat as "loaded — start tracking"
    if (!initialLoadDoneRef.current) {
      // Defer one tick so the post-load state hydration doesn't trip the flag
      const t = setTimeout(() => { initialLoadDoneRef.current = true; }, 200);
      return () => clearTimeout(t);
    }
  }, [isLoading]);

  useEffect(() => {
    if (initialLoadDoneRef.current) lessonDirtyRef.current = true;
  // containerComments must be tracked here too — teacher homework comments
  // are saved as part of this same lesson payload (LessonWorkspace.js
  // `containerComments: containerComments` in the save call), but weren't
  // in this dependency list, so adding a comment alone never marked the
  // lesson dirty. That meant the 30s autosave skipped it, and the
  // unsaved-changes navigation guard never warned before losing it.
  }, [editorContents, whiteboardContents, lessonTitle, homeworkDataCache, containerComments]);

  // Ctrl+S / Cmd+S to save — stable effect ([] deps), always calls latest handleSave via ref
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveRef.current?.();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Auto-save every 30 seconds when dirty and required fields are present
  useEffect(() => {
    const interval = setInterval(() => {
      if (lessonDirtyRef.current && lessonTitle.trim() && studentId && !isLoading) {
        handleSaveRef.current?.({ silent: true });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [lessonTitle, studentId, isLoading]);

  // Warn on tab close / refresh / typing a new URL
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (!lessonDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = ''; // required for Chromium-based browsers
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);

  // Expose a guard global that the Sidebar (and any nav button) can call before
  // changing route. Returns true if navigation may proceed immediately,
  // false if the modal was shown — caller must abort and let the modal drive.
  useEffect(() => {
    window.__novasUnsavedChangesGuard = (proceed) => {
      if (!lessonDirtyRef.current) return true;
      setPendingNavigation(() => proceed);
      setShowSaveChangesModal(true);
      return false;
    };
    return () => { delete window.__novasUnsavedChangesGuard; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intercept browser back / trackpad swipe-back (popstate). BrowserRouter
  // turns these into SPA route changes — by pushing a duplicate state on
  // mount, the first back-gesture lands on a sentinel entry which we
  // immediately replace, then show our save-changes modal instead of leaving.
  useEffect(() => {
    if (isLoading) return; // wait until lesson is loaded
    window.history.pushState({ __novasLessonGuard: true }, '', window.location.href);

    const onPopState = () => {
      if (!lessonDirtyRef.current) return;
      // Re-push so we stay on this page
      window.history.pushState({ __novasLessonGuard: true }, '', window.location.href);
      setPendingNavigation(() => () => {
        lessonDirtyRef.current = false;
        // Now actually go back — our re-push will pop, then the original back
        window.history.go(-1);
      });
      setShowSaveChangesModal(true);
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isLoading]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Функции для обработки навигации с подтверждением сохранения
  const handleNavigationAttempt = (navigationFn) => {
    setShowSaveChangesModal(true);
    setPendingNavigation(() => navigationFn);
  };

  const handleSaveAndNavigate = async () => {
    // Для студентов в режиме homework сохраняем изменения homework
    const currentHomeworkId = searchParams.get('homeworkId') || homeworkId;
    const isFromHomework = searchParams.get('fromHomework') === 'true';

    if (isStudent && (isFromHomework || currentHomeworkId)) {
      // Сохраняем все изменения homework из editorContents
      const hwId = currentHomeworkId || homeworkId;
      if (hwId) {
        const homeworkData = homeworkDataCache[hwId] || {};
        const containerContents = [];

        // Собираем все изменения homework контейнеров
        Object.entries(editorContents).forEach(([key, content]) => {
          if (key.startsWith(`homework-${hwId}-container-`)) {
            const containerId = key.replace(`homework-${hwId}-container-`, '');
            containerContents.push({ id: containerId, content });
          }
        });

        const updatedHomeworkData = {
          ...homeworkData,
          containerContents,
          status: homeworkData.status || 'assigned',
          assignedAt: homeworkData.assignedAt || new Date().toISOString(),
          lessonId: lessonId || homeworkData.lessonId || 'new',
          studentId: studentId || homeworkData.studentId || 'demo-student-1',
          title: homeworkData.title || 'Homework',
          dueDate: homeworkData.dueDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        };

        // Обновляем кеш
        setHomeworkDataCache(prev => ({
          ...prev,
          [hwId]: updatedHomeworkData
        }));

        // Отправляем событие об обновлении homework
        // (данные сохраняются в Supabase через updateHomework)
        window.dispatchEvent(new CustomEvent('homeworkUpdated', {
          detail: { homeworkId: hwId, studentId: updatedHomeworkData.studentId }
        }));
        console.log('Homework updated:', hwId);
      }
    } else {
      // Для преподавателей — сохраняем БЕЗ авто-редиректа на профиль студента,
      // так как у нас есть pendingNavigation в которое мы реально хотим уйти.
      // Must be awaited: handleSave is a real Supabase round-trip, and
      // pendingNavigation() below used to fire immediately after this
      // (fire-and-forget), navigating away before the save had resolved —
      // on a slow connection or backgrounded tab the save (and any error
      // toast for it) could complete after the component had already
      // unmounted, silently dropping the edit.
      await handleSave({ skipNavigate: true });
    }

    setShowSaveChangesModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleNavigateWithoutSaving = () => {
    lessonDirtyRef.current = false; // user opted to discard — let navigation proceed
    setShowSaveChangesModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowSaveChangesModal(false);
    setPendingNavigation(null);
  };

  return {
    addSnapshotToLibrary,
    setAddSnapshotToLibrary,
    handleSave,
    handleNavigationAttempt,
    handleSaveAndNavigate,
    handleNavigateWithoutSaving,
    handleCancelNavigation,
  };
}
